import type { BroadcastQueueItem, BroadcastQueueStatus } from '@/types/broadcastQueue';
import { getPermissionCapabilityMap } from '@/features/permissions/selectors';
import type { PermissionContext } from '@/features/permissions/types';

const broadcastQueueChannelChangeableStatuses: ReadonlySet<BroadcastQueueStatus> = new Set([
  'pending_approval',
  'approved',
  'sent',
  'failed',
]);

const broadcastQueueApproveableStatuses: ReadonlySet<BroadcastQueueStatus> = new Set([
  'pending_approval',
  'failed',
]);

const broadcastQueueCapabilities = [
  'broadcastQueue.manageMode',
  'broadcastQueue.approveSelected',
  'broadcastQueue.approveAll',
  'broadcastQueue.changeChannel',
] as const;

export function getBroadcastQueueCapabilities(context: PermissionContext) {
  const capabilityMap = getPermissionCapabilityMap(broadcastQueueCapabilities, context);

  return {
    canManageMode: capabilityMap['broadcastQueue.manageMode'],
    canApproveSelected: capabilityMap['broadcastQueue.approveSelected'],
    canApproveAll: capabilityMap['broadcastQueue.approveAll'],
    canChangeChannel: capabilityMap['broadcastQueue.changeChannel'],
    canUseSelection:
      capabilityMap['broadcastQueue.approveSelected'] || capabilityMap['broadcastQueue.changeChannel'],
  };
}

export function getSelectedApproveableQueueIds(params: {
  items: BroadcastQueueItem[];
  selectedIds: Iterable<string>;
}): string[] {
  const { items, selectedIds } = params;

  return Array.from(selectedIds).filter((id) =>
    items.some((item) => item.id === id && broadcastQueueApproveableStatuses.has(item.status))
  );
}

export function getSelectedChannelChangeableQueueIds(params: {
  items: BroadcastQueueItem[];
  selectedIds: Iterable<string>;
}): string[] {
  const { items, selectedIds } = params;

  return Array.from(selectedIds).filter((id) =>
    items.some((item) => item.id === id && broadcastQueueChannelChangeableStatuses.has(item.status))
  );
}

export function isBroadcastQueueRowSelectable(item: BroadcastQueueItem): boolean {
  return broadcastQueueChannelChangeableStatuses.has(item.status);
}

export function countChannelChangeableQueueItems(items: BroadcastQueueItem[]): number {
  return items.filter(isBroadcastQueueRowSelectable).length;
}
